import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import useLocalStorageState from '@hooks/useLocalStorageState';

export type TerritoryMode = 'state' | 'metro';

export interface SavedSelection {
    mode: TerritoryMode;
    ids: string[];
}

const MODE_STORAGE_KEY = 'home-territory-saved-mode';
const IDS_STORAGE_KEY = 'home-territory-saved-ids';
const ALL_TERRITORIES_TOKEN = '__ALL__';

interface TerritorySelectionValue {
    mode: TerritoryMode;
    stateIds: string[];
    metroIds: string[];
    selectedIds: string[];
    setMode: (m: TerritoryMode) => void;
    setStateIds: (ids: string[]) => void;
    setMetroIds: (ids: string[]) => void;
    setSelectedIds: (ids: string[]) => void;
    savedSelection: SavedSelection | null;
    saveSelection: (mode: TerritoryMode, ids: string[]) => void;
    clearSavedSelection: () => void;
    resetToSaved: () => void;
}

const TerritorySelectionContext = createContext<TerritorySelectionValue | null>(null);

export const TerritorySelectionProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [savedMode, setSavedModeRaw] = useLocalStorageState<TerritoryMode>(
        MODE_STORAGE_KEY,
        'state',
        (raw) => (raw === 'metro' ? 'metro' : 'state'),
    );
    const [idsRaw, setIdsRaw] = useLocalStorageState<string>(IDS_STORAGE_KEY, '');

    const savedSelection = useMemo<SavedSelection | null>(() => {
        if (!idsRaw) return null;
        if (idsRaw === ALL_TERRITORIES_TOKEN) return { mode: savedMode, ids: [] };
        return { mode: savedMode, ids: idsRaw.split(',') };
    }, [savedMode, idsRaw]);

    const [mode, setMode] = useState<TerritoryMode>(() => savedSelection?.mode ?? 'state');
    const [stateIds, setStateIds] = useState<string[]>(
        () => (savedSelection?.mode === 'state' ? savedSelection.ids : [])
    );
    const [metroIds, setMetroIds] = useState<string[]>(
        () => (savedSelection?.mode === 'metro' ? savedSelection.ids : [])
    );

    const selectedIds = useMemo(() => {
        return mode === 'state' ? stateIds : metroIds
    }, [mode, stateIds, metroIds]);

    const setSelectedIds = useCallback((ids: string[]) => {
        if (mode === 'state') setStateIds(ids);
        else setMetroIds(ids);
    }, [mode]);

    const saveSelection = useCallback((m: TerritoryMode, ids: string[]) => {
        setSavedModeRaw(m);
        setIdsRaw(ids.length === 0 ? ALL_TERRITORIES_TOKEN : ids.join(','));
        if (m === 'state') setMetroIds([]);
        else setStateIds([]);
    }, [setSavedModeRaw, setIdsRaw]);

    const clearSavedSelection = useCallback(() => {
        setIdsRaw('');
        setSavedModeRaw('state');
    }, [setIdsRaw, setSavedModeRaw]);

    const resetToSaved = useCallback(() => {
        if (savedSelection) {
            setMode(savedSelection.mode);
            setStateIds(savedSelection.mode === 'state' ? savedSelection.ids : []);
            setMetroIds(savedSelection.mode === 'metro' ? savedSelection.ids : []);
        } else {
            setMode('state');
            setStateIds([]);
            setMetroIds([]);
        }
    }, [savedSelection]);

    return (
        <TerritorySelectionContext.Provider
            value={{
                mode,
                stateIds,
                metroIds,
                selectedIds,
                setMode,
                setStateIds,
                setMetroIds,
                setSelectedIds,
                savedSelection,
                saveSelection,
                clearSavedSelection,
                resetToSaved,
            }}
        >
            {children}
        </TerritorySelectionContext.Provider>
    );
};

export function useTerritorySelection(): TerritorySelectionValue {
    const ctx = useContext(TerritorySelectionContext);
    if (ctx === null) {
        throw new Error('useTerritorySelection must be used inside TerritorySelectionProvider');
    }
    return ctx;
}
